import {
  AssociationWithMembers,
  associationWithMembersSchema,
} from "@boark/shared/types/association-with-members";
import { Register } from "@boark/shared/types/register";

const API_BASE_URL = process.env.API_BASE_URL || "/api";

function getHeaders(language: string): HeadersInit {
  return {
    "Content-Type": "application/json",
    "Accept-Language": language,
  };
}

async function parseError(response: Response): Promise<Error> {
  const body = (await response.json().catch(() => undefined)) as
    | { message?: string }
    | undefined;
  return new Error(body?.message ?? "Request failed");
}

export async function login(
  email: string,
  password: string,
  language: string,
): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/login`, {
    method: "POST",
    headers: getHeaders(language),
    credentials: "include",
    body: JSON.stringify({ email, password }),
  });

  if (!response.ok) {
    throw await parseError(response);
  }
}

export async function logout(): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/logout`, {
    method: "POST",
    credentials: "include",
  });

  if (!response.ok) {
    throw new Error("Failed to logout");
  }
}

export async function register(
  input: Register,
  language: string,
): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/register`, {
    method: "POST",
    headers: getHeaders(language),
    body: JSON.stringify(input),
  });

  if (!response.ok) {
    throw await parseError(response);
  }
}

export async function activateRegistration(
  token: string,
  language: string,
): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/register/activate`, {
    method: "POST",
    headers: getHeaders(language),
    body: JSON.stringify({ token }),
  });

  if (!response.ok) {
    throw await parseError(response);
  }
}

export async function requestPasswordReset(
  email: string,
  language: string,
): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/reset-password`, {
    method: "POST",
    headers: getHeaders(language),
    body: JSON.stringify({ email }),
  });

  if (!response.ok) {
    throw await parseError(response);
  }
}

export async function confirmPasswordReset(
  token: string,
  password: string,
  language: string,
): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/reset-password/confirm`, {
    method: "POST",
    headers: getHeaders(language),
    body: JSON.stringify({ token, password }),
  });

  if (!response.ok) {
    throw await parseError(response);
  }
}

export async function inviteUser(
  associationId: string,
  email: string,
  language: string,
): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/invite`, {
    method: "POST",
    headers: getHeaders(language),
    credentials: "include",
    body: JSON.stringify({ associationId, email }),
  });

  if (!response.ok) {
    throw await parseError(response);
  }
}

export async function acceptInvite(
  token: string,
  password: string,
  language: string,
): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/invite/accept`, {
    method: "POST",
    headers: getHeaders(language),
    body: JSON.stringify({ token, password }),
  });

  if (!response.ok) {
    throw await parseError(response);
  }
}

export async function getOwnedAssociations(
  language: string,
): Promise<AssociationWithMembers[]> {
  const response = await fetch(`${API_BASE_URL}/association/owned`, {
    method: "GET",
    headers: getHeaders(language),
    credentials: "include",
  });

  if (!response.ok) {
    throw await parseError(response);
  }

  return associationWithMembersSchema.array().parse(await response.json());
}
